import {
  useAppDrawerName,
  type AppDrawerName,
} from "@client/utils/exclusiveDrawer";
import { useEffect, useState } from "react";

/**
 * Whether the funder is plausibly LOOKING at the page right now.
 *
 * Both of the rail's dwell timers need this and had drifted apart: the
 * observation dwell checked it, the visit dwell didn't — so switching tabs, or
 * a meeting queue auto-advancing behind an open drawer, could record a place as
 * visited that was never on screen, and push it to the top of History.
 *
 * Two signals, both cheap:
 * - the tab is foregrounded (a background tab still runs timers);
 * - no drawer is covering the page. A surface rendered INSIDE the open drawer
 *   passes its own name as `hostDrawer`: it is the thing being read, not the
 *   thing being covered.
 *
 * Deliberately NOT viewport visibility — that is per-element and belongs to the
 * caller that owns the element (see `useTodoObservation`'s `observe`).
 */
export function usePageAttention(hostDrawer?: AppDrawerName): boolean {
  const openDrawer = useAppDrawerName();
  const foregrounded = useTabForegrounded();

  const coveredByDrawer = openDrawer != null && openDrawer !== hostDrawer;
  return foregrounded && !coveredByDrawer;
}

/**
 * Just "is this tab in front" — no opinion about what is drawn over the page.
 *
 * Split out for the VISIT dwell, which cares whether anyone is at the desk and
 * not whether a panel is covering the page: opening the rail to see what to do
 * next is part of doing it, and treating that as inattention discarded the
 * visit the funder was in the middle of earning. Reading a summary through a
 * drawer is a different question, so `usePageAttention` keeps the coverage
 * test for the observation dwell.
 */
export function useTabForegrounded(): boolean {
  const [foregrounded, setForegrounded] = useState(
    () => typeof document === "undefined" || !document.hidden,
  );

  useEffect(() => {
    const onChange = () => setForegrounded(!document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);

  return foregrounded;
}
